import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  fetchNotifications,
  type FetchNotificationsOptions,
} from '../notifications/notificationFeedClient';
import type { NotificationItem } from '../types/models/Notification';
import { useNotificationCountStore } from './notificationCountStore';

/**
 * Notification feed store — Story 92.2 Task 3.
 *
 * Owns the cursor-paginated visible feed for the slide-out panel. Source of
 * truth for `items`, `nextCursor`, the active tab, and the active project
 * filter. The unread badge count itself lives on `notificationCountStore`
 * (server-authoritative, SSE-synced); the feed store is presentation state
 * and deliberately does NOT keep its own unread counter (BUG-92.2-001 — a
 * second counter drifted from the bell badge).
 *
 * Module Federation rule: consumers MUST import via the absolute path
 * `@helios/shared-domain/stores/notificationFeedStore` (not relative paths)
 * to avoid dual Zustand store instances across the shell/remote boundary.
 */

export type NotificationFeedTab = 'all' | 'unread';

export interface NotificationFeedStore {
  items: NotificationItem[];
  nextCursor: string | null;
  isLoading: boolean;
  error: string | null;
  activeTab: NotificationFeedTab;
  activeProjectId: string | null;
  /**
   * BUG-92.2-002 — server-reported unread count within `activeProjectId`,
   * paging-proof (unread rows beyond the loaded page still count). `null`
   * when no project filter is active or the backend didn't report it. The
   * panel gates the project-scoped "Mark all as read" button on this instead
   * of scanning the loaded rows. Maintained locally between fetches:
   * decremented on `markItemRead`, bumped on `prependItem`, zeroed by a
   * filtered `markAllRead`.
   */
  filteredUnreadCount: number | null;
  /**
   * Set to `true` by `useNotificationStream` when a `notification.created` SSE
   * event arrives while no surface (panel/center) is mounted, so the live
   * prepend is skipped. The surface clears this flag by calling `load()` on
   * open — that's how new notifications appear after closing/re-opening the
   * bell without requiring a full page refresh.
   */
  isStale: boolean;

  load: (opts?: { projectId?: string | null }) => Promise<void>;
  loadMore: () => Promise<void>;
  reset: () => void;
  setTab: (tab: NotificationFeedTab) => void;
  setProjectFilter: (projectId: string | null) => Promise<void>;
  prependItem: (item: NotificationItem) => void;
  markItemRead: (id: string) => void;
  markAllRead: () => void;
  markStale: () => void;
}

const PAGE_LIMIT = 50;

const initialState = {
  items: [] as NotificationItem[],
  nextCursor: null as string | null,
  isLoading: false,
  error: null as string | null,
  activeTab: 'all' as NotificationFeedTab,
  activeProjectId: null as string | null,
  filteredUnreadCount: null as number | null,
  isStale: false,
};

export const useNotificationFeedStore = create<NotificationFeedStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      load: async (opts) => {
        const projectId =
          opts && Object.prototype.hasOwnProperty.call(opts, 'projectId')
            ? (opts.projectId ?? null)
            : get().activeProjectId;
        set(
          {
            items: [],
            nextCursor: null,
            isLoading: true,
            error: null,
            activeProjectId: projectId,
            filteredUnreadCount: null,
            // Loading consumes the staleness signal — the fetched page reflects
            // any SSE arrivals that fired while no surface was mounted.
            isStale: false,
          },
          false,
          'notificationFeed/load/start'
        );
        try {
          const fetchOpts: FetchNotificationsOptions = { limit: PAGE_LIMIT };
          if (projectId) fetchOpts.projectId = projectId;
          const res = await fetchNotifications(fetchOpts);
          set(
            {
              items: res.items,
              nextCursor: res.nextCursor,
              filteredUnreadCount: res.filteredUnreadCount ?? null,
              isLoading: false,
              error: null,
            },
            false,
            'notificationFeed/load/success'
          );
          // Keep the bell badge in sync with the server-authoritative count.
          useNotificationCountStore.getState().setUnreadCount(res.unreadCount);
        } catch (err) {
          set(
            {
              isLoading: false,
              error: err instanceof Error ? err.message : 'Failed to load notifications',
            },
            false,
            'notificationFeed/load/error'
          );
        }
      },

      loadMore: async () => {
        const { nextCursor, isLoading, activeProjectId, items } = get();
        if (!nextCursor || isLoading) return;
        set({ isLoading: true, error: null }, false, 'notificationFeed/loadMore/start');
        try {
          const fetchOpts: FetchNotificationsOptions = {
            limit: PAGE_LIMIT,
            cursor: nextCursor,
          };
          if (activeProjectId) fetchOpts.projectId = activeProjectId;
          const res = await fetchNotifications(fetchOpts);
          // De-dupe in case the cursor boundary overlaps an SSE-prepended item.
          const existing = new Set(items.map((i) => i.id));
          const appended = res.items.filter((i) => !existing.has(i.id));
          set(
            {
              items: [...items, ...appended],
              nextCursor: res.nextCursor,
              filteredUnreadCount: res.filteredUnreadCount ?? null,
              isLoading: false,
              error: null,
            },
            false,
            'notificationFeed/loadMore/success'
          );
          // Keep the bell badge in sync with the server-authoritative count.
          useNotificationCountStore.getState().setUnreadCount(res.unreadCount);
        } catch (err) {
          set(
            {
              isLoading: false,
              error: err instanceof Error ? err.message : 'Failed to load more notifications',
            },
            false,
            'notificationFeed/loadMore/error'
          );
        }
      },

      reset: () => set({ ...initialState }, false, 'notificationFeed/reset'),

      setTab: (tab) => set({ activeTab: tab }, false, 'notificationFeed/setTab'),

      setProjectFilter: async (projectId) => {
        if (get().activeProjectId === projectId) return;
        await get().load({ projectId });
      },

      prependItem: (item) => {
        const { items, activeProjectId, filteredUnreadCount } = get();
        if (items.some((i) => i.id === item.id)) return;
        // Callers only prepend items matching the active filter, so an unread
        // arrival grows the filtered residual too.
        const nextFiltered =
          activeProjectId && filteredUnreadCount !== null && !item.readAt
            ? filteredUnreadCount + 1
            : filteredUnreadCount;
        set(
          { items: [item, ...items], filteredUnreadCount: nextFiltered },
          false,
          'notificationFeed/prependItem'
        );
      },

      markItemRead: (id) => {
        const { items, activeProjectId, filteredUnreadCount } = get();
        const idx = items.findIndex((i) => i.id === id);
        if (idx === -1) return;
        const target = items[idx];
        if (target.readAt) return;
        const next = [...items];
        next[idx] = { ...target, readAt: new Date().toISOString() };
        // Best-effort local maintenance — there is no SSE fan-out for the
        // filtered count, and the marked row belongs to the active filter by
        // construction (it is in the filtered list).
        const nextFiltered =
          activeProjectId && filteredUnreadCount !== null
            ? Math.max(0, filteredUnreadCount - 1)
            : filteredUnreadCount;
        set(
          { items: next, filteredUnreadCount: nextFiltered },
          false,
          'notificationFeed/markItemRead'
        );
      },

      markAllRead: () => {
        const now = new Date().toISOString();
        const { items, activeProjectId } = get();
        if (!items.every((i) => i.readAt)) {
          set(
            { items: items.map((i) => (i.readAt ? i : { ...i, readAt: now })) },
            false,
            'notificationFeed/markAllRead'
          );
        }
        // BUG-92.2-001: only an UNFILTERED read-all clears the whole inbox, so
        // only then may the authoritative count be assigned to 0 (callers
        // invoke this after the server confirmed the bulk read). A
        // project-scoped read-all clears just that project's subset — the
        // residual global count arrives via the SSE `unread_count` fan-out.
        if (!activeProjectId) {
          useNotificationCountStore.getState().setUnreadCount(0);
        } else {
          // BUG-92.2-002: the server cleared the ENTIRE project, including
          // unread rows beyond the loaded page — the filtered residual is 0.
          set({ filteredUnreadCount: 0 }, false, 'notificationFeed/markAllRead/filteredCount');
        }
      },

      markStale: () => set({ isStale: true }, false, 'notificationFeed/markStale'),
    }),
    { name: 'notificationFeedStore' }
  )
);
